const REF_ATTR = 'data-ref';
const ON_PREFIX = 'on:';

const EVENTS = ['click', 'change', 'input', 'submit', 'keydown', 'keyup', 'toggle'];
const NON_BUBBLING_EVENTS = new Set(['focus', 'blur']);
const EXPENSIVE_EVENTS = new Set(['pointerenter', 'pointerleave']);

let proxyInstalled = false;

function installEventProxy() {
  if (proxyInstalled) return;
  proxyInstalled = true;

  for (const name of [...EVENTS, ...NON_BUBBLING_EVENTS, ...EXPENSIVE_EVENTS]) {
    document.addEventListener(name, handleDelegatedEvent, { capture: true });
  }
}

function handleDelegatedEvent(event) {
  const element = getElement(event);
  if (!element) return;

  const attrName = ON_PREFIX + event.type;
  const value = element.getAttribute(attrName) ?? '';
  if (!value) return;

  const lastSlash = value.lastIndexOf('/');

  let methodName;
  let dataStr;

  if (lastSlash !== -1) {
    const afterSlash = value.substring(lastSlash + 1);
    const qIdx = afterSlash.indexOf('?');
    if (qIdx !== -1) {
      methodName = afterSlash.substring(0, qIdx);
      dataStr = afterSlash.substring(qIdx + 1);
    } else {
      methodName = afterSlash;
    }
  } else {
    const qIdx = value.indexOf('?');
    if (qIdx !== -1) {
      methodName = value.substring(0, qIdx);
      dataStr = value.substring(qIdx + 1);
    } else {
      methodName = value;
    }
  }

  const component = findComponentWithMethod(element, methodName);
  if (!component) return;

  const method = component[methodName];

  const data = {};
  if (dataStr) {
    const params = new URLSearchParams(dataStr);
    for (const [key, val] of params) {
      data[key] = parseValue(val);
    }
  }

  const proxiedEvent = event.target !== element
    ? new Proxy(event, {
        get(target, prop) {
          if (prop === 'target') return element;
          if (prop === 'currentTarget') return element;
          const value = Reflect.get(target, prop);
          return typeof value === 'function' ? value.bind(target) : value;
        },
      })
    : event;

  try {
    method.call(component, proxiedEvent, data);
  } catch (error) {
    console.error(error);
  }
}

function getElement(event) {
  const target = event.composedPath?.()[0] ?? event.target;

  if (!(target instanceof Element)) return null;

  const attrName = ON_PREFIX + event.type;

  if (target.hasAttribute(attrName)) {
    return target;
  }

  if (EXPENSIVE_EVENTS.has(event.type)) {
    const path = event.composedPath();
    if (path) {
      for (let i = 1; i < path.length; i++) {
        const el = path[i];
        if (el instanceof Element && el.hasAttribute(attrName)) {
          return el;
        }
      }
    }
    return null;
  }

  const bubbles = event.bubbles || NON_BUBBLING_EVENTS.has(event.type);
  if (!bubbles) return null;

  return target.closest(`[on\\:${event.type}]`);
}

function parseValue(val) {
  if (val === 'true') return true;
  if (val === 'false') return false;
  const num = Number(val);
  if (!Number.isNaN(num) && val !== '') return num;
  return val;
}

function findComponentWithMethod(el, methodName) {
  let current = el;
  while (current) {
    if ((current instanceof Component || (current.tagName && current.tagName.endsWith('-COMPONENT')))
        && typeof current[methodName] === 'function') {
      return current;
    }
    current = current.parentElement || current.host;
  }
  return null;
}

export class Component extends HTMLElement {
  refs = {};
  requiredRefs;
  #mutationObserver;

  connectedCallback() {
    this.#updateRefs();
    if (this.requiredRefs) {
      for (const name of this.requiredRefs) {
        if (!this.refs[name]) {
          console.warn(`<${this.tagName.toLowerCase()}>: missing required ref "${name}"`);
        }
      }
    }
    this.#mutationObserver = new MutationObserver(() => this.#updateRefs());
    if (window.requestIdleCallback) {
      requestIdleCallback(() => {
        if (!this.isConnected) return;
        this.#mutationObserver.observe(this, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: [REF_ATTR],
        });
      });
    } else {
      this.#mutationObserver.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: [REF_ATTR],
      });
    }

    installEventProxy();
  }

  disconnectedCallback() {
    this.#mutationObserver?.disconnect();
  }

  updatedCallback() {
    this.#updateRefs();
  }

  #updateRefs() {
    const refs = {};
    for (const el of this.querySelectorAll(`[${REF_ATTR}]`)) {
      const name = el.getAttribute(REF_ATTR);
      if (!name) continue;
      const existing = refs[name];
      if (existing) {
        refs[name] = Array.isArray(existing)
          ? [...existing, el]
          : [existing, el];
      } else {
        refs[name] = el;
      }
    }
    this.refs = refs;
  }
}
